import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import OnboardingTaskSetup, { DefaultTask } from '@/components/OnboardingTaskSetup';
import { generateTeamName } from '@/lib/locationData';
import { useOnboardingState } from '@/hooks/useOnboardingState';
import { useOnboardingActions } from '@/hooks/useOnboardingActions';
import { OrganizationActionSelector } from '@/components/onboarding/OrganizationActionSelector';
import { OrganizationCreationForm } from '@/components/onboarding/OrganizationCreationForm';
import { getStepCount } from '@/utils/onboardingValidation';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { state, updateState } = useOnboardingState();
  const { searchOrganizations, validateOrganizationNameAjax, handleOrganizationSelect, handleNext, handleBack } = useOnboardingActions(state, updateState);
  
  // Redirect to auth if no user
  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth', { replace: true });
    }
  }, [user, loading, navigate]);

  // Handle search input changes with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (state.searchQuery && state.selectedAction === 'join') {
        searchOrganizations(state.searchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [state.searchQuery, state.selectedAction]);

  // Handle organization name validation with debounce (AJAX)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (state.organizationName && state.selectedAction === 'create') {
        validateOrganizationNameAjax(state.organizationName);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [state.organizationName, state.selectedAction]);

  // Generate team name when location selection changes
  useEffect(() => {
    if (state.selectedCountry && state.selectedState && state.selectedCity && state.selectedAction === 'create') {
      const teamName = generateTeamName(state.organizationName, state.selectedCountry, state.selectedState, state.selectedCity);
      updateState({ generatedTeamName: teamName });
    } else {
      updateState({ generatedTeamName: '' });
    }
  }, [state.selectedCountry, state.selectedState, state.selectedCity, state.selectedAction, state.organizationName]);



  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Card className="shadow-lg border bg-card">
          <CardContent className="p-6">
            {/* Step 1: Choose Action */}
            {state.currentStep === 1 && (
              <OrganizationActionSelector
                state={state}
                updateState={updateState}
                handleOrganizationSelect={handleOrganizationSelect}
              />
            )}

            {/* Step 2: Organization Details (Create) */}
            {state.currentStep === 2 && state.selectedAction === 'create' && (
              <OrganizationCreationForm
                state={state}
                updateState={updateState}
              />
            )}

            {/* Step 2: Join Team Confirmation */}
            {state.currentStep === 2 && state.selectedAction === 'join' && (
              <div className="space-y-6">
                <div className="mx-auto h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  {state.selectedOrganization?.logo_url ? (
                    <img 
                      src={state.selectedOrganization.logo_url} 
                      alt={`${state.selectedOrganization.name} Logo`}
                      className="h-8 w-8 object-contain"
                    />
                  ) : (
                    <Building2 className="h-8 w-8 text-primary" />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">Team-Beitritt bestätigen</h3>
                <p className="text-muted-foreground">
                  Sind Sie sicher, dass Sie diesem Team beitreten möchten?
                </p>

                {state.selectedOrganization && (
                    <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <h4 className="text-lg font-medium">{state.selectedOrganization.name}</h4>
                          <div className="text-sm text-muted-foreground">
                            {state.selectedOrganization.type === 'vodafone' ? 'Vodafone Organisation' : 'Benutzerdefinierte Organisation'}
                          </div>
                        </div>
                        <div className="flex justify-center items-center">
                        </div>
                      </div>
                    
                    <div className="bg-background rounded-md p-4 border-l-4 border-primary">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Als Teammitglied werden Sie:</p>
                          <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                            <li>• Zugang zu Teamaufgaben und -bereichen erhalten</li>
                            <li>• Mit anderen Teammitgliedern zusammenarbeiten</li>
                            <li>• Die Organisationsrichtlinien befolgen</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Default Tasks (Create only) */}
            {state.currentStep === 3 && state.selectedAction === 'create' && (
              <OnboardingTaskSetup
                tasks={state.defaultTasks}
                onTasksChange={(tasks) => updateState({ defaultTasks: tasks })}
                onComplete={handleNext}
                onSkip={handleNext}
              />
            )}

            {/* Navigation Buttons - Only show for steps that need manual navigation */}
            {(state.currentStep === 1 || (state.currentStep === 2 && state.selectedAction === 'join')) && (
              <div className="flex justify-between pt-6">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="flex items-center gap-2"
                  disabled={state.currentStep === 1}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Zurück
                </Button>

                <Button
                  onClick={handleNext}
                  className="flex items-center gap-2"
                  disabled={state.isProcessing}
                >
                  {state.isProcessing ? 'Verarbeitung...' : 'Weiter'}
                  {!state.isProcessing && <ArrowRight className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;